import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Stack,
} from '@mui/material';
import { 
  ArrowBack, 
  ExpandMore, 
  Download,
  Save,
  Logout,
  Info,
  Create,
  DirectionsCar,
  LockOpen,
  Warning,
  Edit,
  Add,
  Delete,
  SwapHoriz
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import { generateEScoreCardPDF } from '../services/pdfService';
import { getAnnotationImageFromS3, uploadAnnotationToS3, uploadPdfToS3, generatePdfFilename, generateAnnotationFilename, createEScoreCardInUCS, uploadDocumentToUCS } from '../services/motorApiService';
import ImageAnnotationPanel from '../components/ImageAnnotationPanel';

// Styled components for consistent modern design across all sections
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: 'transparent',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.1)',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::before': {
    content: '""',
    width: 4,
    height: 28,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const SubSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::before': {
    content: '""',
    width: 3,
    height: 20,
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 2,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: theme.spacing(1.5),
    fontSize: '0.95rem',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(1.5),
    fontSize: '0.95rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: theme.spacing(1.5),
    fontSize: '0.95rem',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  '& .MuiSelect-select': {
    padding: theme.spacing(1.5),
    fontSize: '0.95rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: theme.spacing(1.5),
    minHeight: 56,
    '&.Mui-expanded': {
      minHeight: 56,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  '& .MuiAccordionDetails-root': {
    padding: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
  },
}));

const InfoGrid = styled(Grid)(({ theme }) => ({
  '& .MuiGrid-item': {
    display: 'flex',
    flexDirection: 'column',
  },
}));


const InspectionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const vehicleData = location.state?.vehicle;

  const [formItems, setFormItems] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState({}); // Track selected items per category
  const [inspectionData, setInspectionData] = useState({
    contractNo: vehicleData?.ContractNo || '',
    vehicleId: vehicleData?.VehicleID || null,
    make: vehicleData?.Make || '',
    model: vehicleData?.Model || '',
    carInDate: vehicleData?.CarInDate || '',
    licenseNo: vehicleData?.LicenseNo || '',
    cylinderCapacity: vehicleData?.CylinderCapacity || '',
    year: vehicleData?.ManufacturingYear || '',
    mileage: vehicleData?.Mileage || '',
    numberOfOwners: vehicleData?.NoOfPreviousOwners || '0',
    exteriorScore: 5,
    interiorGrade: 'A',
    details: {}
  });
  
  const [annotatedImage, setAnnotatedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasUnsavedAnnotations, setHasUnsavedAnnotations] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [wasOriginallySubmitted, setWasOriginallySubmitted] = useState(false);
  const [isReInspection, setIsReInspection] = useState(false);
  const [inspectionId, setInspectionId] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockingForEdit, setUnlockingForEdit] = useState(false);
  const [isUnlockedForEditing, setIsUnlockedForEditing] = useState(false);
  
  // Unified notification system - only one message at a time
  const [notification, setNotification] = useState({ type: '', message: '' });

  useEffect(() => {
    if (!vehicleData) {
      navigate('/');
      return;
    }
    // Check if inspection is already submitted based on location.state
    const submissionStatus = location.state?.isSubmitted || false;
    
    setIsSubmitted(submissionStatus);
    setWasOriginallySubmitted(submissionStatus); // Track original submission status
    setShowDownloadButton(submissionStatus); // Show download button if already submitted
    
    // Show notification if inspection is already submitted
    if (submissionStatus) {
      setNotification({ 
        type: 'info', 
        message: 'This inspection has already been submitted. Click Re-Inspect to make changes.' 
      });
    }
    
    loadFormItems();
  }, [vehicleData, navigate, location.state]);

  const loadFormItems = async () => {
    try {
      // Only load form items from API for Not Submitted records
      const submissionStatus = location.state?.isSubmitted || false;
      
      if (submissionStatus) {
        // For Submitted records, load inspection details from MM database
        try {
          const detailsData = await apiService.getSubmittedInspectionDetails(vehicleData.ContractNo);
          
          if (detailsData && detailsData.length > 0) {
            // Build categories and formItems from the response
            const categoriesMap = {};
            const builtFormItems = {};
            const builtDetails = {};
            const builtSelectedItems = {};

            // Only display items with Count > 0 for submitted inspections
            const filteredData = detailsData.filter(item => item.Count > 0);

            filteredData.forEach(item => {
              const catName = item.CategoryName;

              // Build categories
              if (!categoriesMap[item.CategoryId]) {
                categoriesMap[item.CategoryId] = {
                  Id: item.CategoryId,
                  Name: catName,
                  DisplayOrder: item.CategoryId
                };
              }

              // Build formItems grouped by category
              if (!builtFormItems[catName]) {
                builtFormItems[catName] = [];
              }
              // Avoid duplicates
              if (!builtFormItems[catName].find(fi => fi.Id === item.FormItemId)) {
                builtFormItems[catName].push({
                  Id: item.FormItemId,
                  CategoryId: item.CategoryId,
                  Category: catName,
                  DamageCode: item.DamageCode,
                  Description: item.Description,
                  Weight: item.Weight
                });
              }

              // Build details
              builtDetails[item.FormItemId] = {
                formItemId: item.FormItemId,
                categoryId: item.CategoryId,
                category: catName,
                damageCode: item.DamageCode,
                description: item.Description,
                weight: item.Weight,
                count: item.Count,
                scoreDeduction: item.ScoreDeduction
              };

              // Build selectedItems
              if (!builtSelectedItems[catName]) {
                builtSelectedItems[catName] = [];
              }
              if (!builtSelectedItems[catName].includes(item.FormItemId)) {
                builtSelectedItems[catName].push(item.FormItemId);
              }
            });

            const sortedCategories = Object.values(categoriesMap).sort((a, b) => a.Id - b.Id);
            setCategories(sortedCategories);
            setFormItems(builtFormItems);
            setSelectedItems(builtSelectedItems);

            // Use first record for header-level fields (from original unfiltered data)
            const first = detailsData[0];
            setInspectionData(prev => ({
              ...prev,
              contractNo: first.ContractNo,
              make: first.Make,
              model: first.Model,
              carInDate: first.CarInDate,
              licenseNo: first.LicenseNo,
              cylinderCapacity: first.CylinderCapacity,
              year: first.ManufacturingYear,
              mileage: first.Mileage,
              numberOfOwners: first.NoOfPreviousOwners,
              exteriorScore: first.ExteriorScore,
              interiorGrade: first.InteriorGrade,
              updatedAt: first.UpdatedAt,
              details: builtDetails
            }));

            console.log('Submitted inspection loaded:', sortedCategories.length, 'categories,', detailsData.length, 'items');
            
            // Store inspectionId for re-inspection
            setInspectionId(first.InspectionId);
          }
        } catch (err) {
          console.error('Failed to load submitted inspection details:', err);
          setNotification({ type: 'error', message: 'Failed to load submitted inspection details' });
        }
        setLoading(false);
        return;
      }
      
      // Fetch form items and categories in a single API call
      const result = await apiService.getFormItemsAndCategories();
      
      setFormItems(result.formItems);
      setCategories(result.categories || []);
      
      // Initialize selected items per category (default to first 2 items)
      const initialSelectedItems = {};
      const initialDetails = {};
      
      Object.keys(result.formItems).forEach(category => {
        const categoryItems = result.formItems[category];
        // Default to first 2 items per category
        const defaultSelectedItems = categoryItems.slice(0, 2).map(item => item.Id);
        initialSelectedItems[category] = defaultSelectedItems;
        
        // Initialize details for all items (but only selected ones will be displayed)
        categoryItems.forEach(item => {
          initialDetails[item.Id] = {
            formItemId: item.Id,
            categoryId: item.CategoryId,
            category: item.Category,
            damageCode: item.DamageCode,
            description: item.Description,
            weight: item.Weight,
            count: 0,
            scoreDeduction: 0
          };
        });
      });
      
      setSelectedItems(initialSelectedItems);
      
      setInspectionData(prev => ({
        ...prev,
        details: initialDetails
      }));
    } catch (err) {
      console.error('Failed to load form items:', err);
      setNotification({ type: 'error', message: 'Failed to load form items' });
    } finally {
      setLoading(false);
    }
  };

  const handleDetailChange = (formItemId, count) => {
    const detail = inspectionData.details[formItemId];
    const scoreDeduction = count * detail.weight;
    
    setInspectionData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [formItemId]: {
          ...detail,
          count: parseInt(count) || 0,
          scoreDeduction: scoreDeduction
        }
      }
    }));
  };

  // Calculate category total with MIN function
  const calculateCategoryTotal = (categoryName) => {
    const categoryItems = selectedItems[categoryName] || [];
    const categoryDeductions = categoryItems.map(itemId => 
      inspectionData.details[itemId]?.scoreDeduction || 0
    );
    const sum = categoryDeductions.reduce((total, deduction) => total + deduction, 0);
    return Math.min(sum, 0.33); // MAX 0.33 per category
  };

  const calculateExteriorScore = () => {
    // Sum all category totals (each capped at 0.33)
    const totalDeduction = categories.reduce((sum, category) => {
      return sum + calculateCategoryTotal(category.Name);
    }, 0);
    const score = Math.max(0, 5 - totalDeduction);
    return Math.round(score * 100) / 100;
  };

  // Helper functions for managing selected items
  const addItemToCategory = (categoryName, itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [categoryName]: [...(prev[categoryName] || []), itemId]
    }));
  };

  const removeItemFromCategory = (categoryName, itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [categoryName]: (prev[categoryName] || []).filter(id => id !== itemId)
    }));
    // Reset the count and score deduction when removing item
    setInspectionData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [itemId]: {
          ...prev.details[itemId],
          count: 0,
          scoreDeduction: 0
        }
      }
    }));
  };

  const updateSelectedItem = (categoryName, oldItemId, newItemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [categoryName]: (prev[categoryName] || []).map(id => id === oldItemId ? newItemId : id)
    }));
    // Reset the old item's count and copy count to new item if needed
    const oldItemCount = inspectionData.details[oldItemId]?.count || 0;
    setInspectionData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [oldItemId]: {
          ...prev.details[oldItemId],
          count: 0,
          scoreDeduction: 0
        },
        [newItemId]: {
          ...prev.details[newItemId],
          count: oldItemCount,
          scoreDeduction: oldItemCount * prev.details[newItemId].weight
        }
      }
    }));
  };

  useEffect(() => {
    // Don't auto-calculate for submitted inspections - use saved value
    if (isSubmitted && wasOriginallySubmitted && !isReInspection) return;
    
    const newScore = calculateExteriorScore();
    setInspectionData(prev => ({
      ...prev,
      exteriorScore: newScore
    }));
  }, [inspectionData.details, selectedItems]);

  const handleUnlock = async () => {
    setUnlocking(true);
    setNotification({ type: '', message: '' });

    try {
      // Load fresh form items (blank form without counts)
      const result = await apiService.getFormItemsAndCategories();
      
      setFormItems(result.formItems);
      setCategories(result.categories || []);
      
      // Initialize selected items and details (blank form)
      const initialSelectedItems = {};
      const initialDetails = {};
      
      Object.keys(result.formItems).forEach(category => {
        const categoryItems = result.formItems[category];
        const defaultSelectedItems = categoryItems.slice(0, 2).map(item => item.Id);
        initialSelectedItems[category] = defaultSelectedItems;
        
        categoryItems.forEach(item => {
          initialDetails[item.Id] = {
            formItemId: item.Id,
            categoryId: item.CategoryId,
            category: item.Category,
            damageCode: item.DamageCode,
            description: item.Description,
            weight: item.Weight,
            count: 0,
            scoreDeduction: 0
          };
        });
      });
      
      setSelectedItems(initialSelectedItems);
      
      // Reset inspection data: keep vehicle info, clear scores and details
      setInspectionData(prev => ({
        ...prev,
        exteriorScore: 5,
        interiorGrade: 'A',
        updatedAt: null, // Will use current date for re-inspection
        details: initialDetails
      }));
      
      setIsSubmitted(false);
      setIsReInspection(true);
      
      setNotification({ type: 'info', message: 'Inspection unlocked for re-inspection. Please complete the assessment and submit again.' });
    } catch (err) {
      console.error('Unlock inspection error:', err);
      setNotification({ type: 'error', message: 'Failed to unlock inspection' });
    } finally {
      setUnlocking(false);
    }
  };

  const handleUnlockForEditing = async () => {
    setUnlockingForEdit(true);
    setNotification({ type: '', message: '' });

    try {
      // Load full form items (all categories and items) so user can add/change items
      const result = await apiService.getFormItemsAndCategories();

      // Build a complete details map with all form items initialized to count 0
      const allDetails = {};
      Object.keys(result.formItems).forEach(category => {
        const categoryItems = result.formItems[category];
        categoryItems.forEach(item => {
          allDetails[item.Id] = {
            formItemId: item.Id,
            categoryId: item.CategoryId,
            category: item.Category,
            damageCode: item.DamageCode,
            description: item.Description,
            weight: item.Weight,
            count: 0,
            scoreDeduction: 0
          };
        });
      });

      // Overlay existing submitted data onto the full details map
      // This preserves the submitted counts for items that were previously submitted
      const existingDetails = inspectionData.details;
      Object.keys(existingDetails).forEach(itemId => {
        const existing = existingDetails[itemId];
        if (allDetails[itemId]) {
          allDetails[itemId] = {
            ...allDetails[itemId],
            count: existing.count || 0,
            scoreDeduction: existing.scoreDeduction || 0
          };
        } else {
          // Item exists in submitted data but not in current form items - keep it
          allDetails[itemId] = existing;
        }
      });

      // Keep the current selectedItems (submitted items with count > 0)
      // but ensure they reference valid items from the full form items list
      const currentSelectedItems = { ...selectedItems };

      // Update formItems to include all available items (so user can add new ones)
      setFormItems(result.formItems);
      setCategories(result.categories || []);
      setSelectedItems(currentSelectedItems);

      // Update inspection data: keep existing scores and details with submitted values
      setInspectionData(prev => ({
        ...prev,
        details: allDetails
      }));

      // Unlock the form for editing
      setIsSubmitted(false);
      setIsReInspection(true);
      setIsUnlockedForEditing(true);

      setNotification({ type: 'info', message: 'Inspection unlocked for editing. You can modify the existing assessment and submit again.' });
    } catch (err) {
      console.error('Unlock for editing error:', err);
      setNotification({ type: 'error', message: 'Failed to unlock inspection for editing' });
    } finally {
      setUnlockingForEdit(false);
    }
  };

  const handleSubmit = async () => {
    if (hasUnsavedAnnotations) {
      setNotification({ type: 'error', message: 'Please save the Damage Annotation image before submitting the inspection.' });
      return;
    }

    setSaving(true);
    setNotification({ type: '', message: '' });

    try {
      if (isReInspection) {
        // === RE-INSPECTION FLOW ===
        console.log('Re-inspection submission for:', inspectionData.contractNo);

        // Step 1: Fetch annotation image from S3
        console.log('Re-inspection Step 1: Fetching annotation image from S3...');
        const annotationImageBase64 = await getAnnotationImageFromS3(inspectionData.vehicleId);

        // Step 2: Generate new PDF
        console.log('Re-inspection Step 2: Generating PDF...');
        const pdfBlob = await generateEScoreCardPDF(inspectionData, annotationImageBase64);
        console.log('PDF generated, size:', pdfBlob.size);

        // Step 3: Upload PDF to S3
        console.log('Re-inspection Step 3: Uploading PDF to S3...');
        const pdfUploadResult = await uploadPdfToS3(inspectionData.contractNo, pdfBlob);
        console.log('PDF uploaded:', pdfUploadResult.filename);

        // Step 3b: Create/Update eScoreCard in UCS
        console.log('Re-inspection Step 3b: Updating eScoreCard in UCS...');
        const ucsResult = await createEScoreCardInUCS({
          ContractNo: inspectionData.contractNo,
          ExteriorScore: inspectionData.exteriorScore,
          InteriorGrade: inspectionData.interiorGrade
        });
        console.log('eScoreCard updated in UCS:', ucsResult.eScoreCardNumber);

        // Step 3c: Upload PDF to UCS
        console.log('Re-inspection Step 3c: Uploading PDF to UCS...');
        await uploadDocumentToUCS({
          eScoreCardNo: ucsResult.eScoreCardNumber,
          filename: pdfUploadResult.filename,
          pdfBlob: pdfBlob
        });
        console.log('PDF uploaded to UCS');

        // Step 4: Update InspectionRecord in MM (esc-4)
        console.log('Re-inspection Step 4: Updating inspection record...');
        const now = new Date();
        const pad = (n, len = 2) => String(n).padStart(len, '0');
        const updatedAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.${pad(now.getMilliseconds(), 3)}`;
        const annotationFilename = generateAnnotationFilename(inspectionData.vehicleId);
        const pdfFilename = generatePdfFilename(inspectionData.contractNo);

        await apiService.updateInspectionRecord({
          Id: inspectionId,
          ContractNo: inspectionData.contractNo,
          Make: inspectionData.make,
          Model: inspectionData.model,
          CarInDate: inspectionData.carInDate,
          LicenseNo: inspectionData.licenseNo,
          CylinderCapacity: inspectionData.cylinderCapacity,
          ManufacturingYear: inspectionData.year,
          Mileage: inspectionData.mileage,
          NoOfPreviousOwners: inspectionData.numberOfOwners,
          ExteriorScore: inspectionData.exteriorScore,
          InteriorGrade: inspectionData.interiorGrade,
          AnnotatedImagePath: annotationFilename,
          PdfPath: pdfFilename,
          Inspector: user?.name || user?.email || null,
          InspectorId: null,
          InspectorEmail: user?.email || null,
          UpdatedAt: updatedAt,
          UpdatedBy: user?.name || user?.email || 'Admin'
        });
        console.log('Inspection record updated');

        // Step 5: Remove old InspectionDetails (esc-7)
        console.log('Re-inspection Step 5: Removing old inspection details...');
        await apiService.removeInspectionDetails(inspectionData.contractNo);
        console.log('Old inspection details removed');

        // Step 6: Create new InspectionDetails (only items with count > 0)
        const detailsWithCount = Object.values(inspectionData.details).filter(d => d.count > 0);
        if (detailsWithCount.length > 0) {
          console.log('Re-inspection Step 6: Creating new inspection details...', detailsWithCount.length, 'items');
          await apiService.createInspectionDetails({
            ContractNo: inspectionData.contractNo,
            InspectionId: inspectionId,
            details: detailsWithCount
          });
          console.log('New inspection details created');
        }

        setNotification({ type: 'success', message: 'Re-inspection submitted successfully!' });
        setIsSubmitted(true);
        setIsReInspection(false);
        setIsUnlockedForEditing(false);
        setWasOriginallySubmitted(true);
        setShowDownloadButton(true);

      } else {
        // === FIRST SUBMISSION FLOW ===
        console.log('Submitting inspection data:', {
          contractNo: inspectionData.contractNo,
          make: inspectionData.make,
          model: inspectionData.model,
          exteriorScore: inspectionData.exteriorScore,
          interiorGrade: inspectionData.interiorGrade,
          details: inspectionData.details
        });

        const response = await apiService.createInspection(inspectionData, user);
        console.log('Inspection submitted successfully:', response);
        
        // Store inspectionId from ESC-2 response for future re-inspection/unlock-for-editing
        if (response.inspectionId) {
          setInspectionId(response.inspectionId);
        }
        
        setNotification({ 
          type: 'success', 
          message: `Inspection submitted successfully! eScoreCard No: ${response.eScoreCardNumber}` 
        });
        
        setIsSubmitted(true);
        setWasOriginallySubmitted(true);
        setShowDownloadButton(true);
      }
    } catch (err) {
      console.error('Inspection submission error:', err);
      
      let errorMessage = 'Failed to save inspection';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    setNotification({ type: '', message: '' }); // Clear current notification

    try {
      const pdfBlob = await apiService.downloadPDF(inspectionData.contractNo);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      // Sanitize contract number for filename (UCC/P00001/2025 -> P00001_2025)
      const sanitizedContractNo = inspectionData.contractNo.replace(/UCC\/P/g, 'P').replace(/\//g, '_');
      link.download = `eScoreCard_${sanitizedContractNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setNotification({ type: 'success', message: `PDF downloaded successfully: eScoreCard_${sanitizedContractNo}.pdf` });
    } catch (err) {
      console.error('PDF download error:', err);
      
      // Create detailed error message
      let errorMessage = 'Failed to download PDF';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 404) {
        errorMessage = 'PDF file not found. Please submit the inspection first.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred while generating PDF. Please try again.';
      } else if (err.code === 'NETWORK_ERROR') {
        errorMessage = 'Network connection failed. Please check your internet connection.';
      }
      
      setNotification({ type: 'error', message: errorMessage });
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Vehicle Inspection - {inspectionData.contractNo}
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            Welcome, {user?.name || user?.email}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>

        {/* Enhanced Vehicle Information */}
        <StyledPaper elevation={4}>
          <SectionTitle variant="h5">
            <DirectionsCar sx={{ fontSize: 28 }} />
            車輛資訊 Vehicle Information
          </SectionTitle>
          
          <InfoGrid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Make 品牌"
                value={inspectionData.make || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Model 型號"
                value={inspectionData.model || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Manufacturing Year 製造年份"
                value={inspectionData.year || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Cylinder Capacity 引擎容積"
                value={inspectionData.cylinderCapacity || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Mileage 里數"
                value={inspectionData.mileage || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Number of Owners 手數"
                value={inspectionData.numberOfOwners || '0'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Contract No. 合約編號"
                value={inspectionData.contractNo || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="License No. 車牌號碼"
                value={inspectionData.licenseNo || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Car-In Date 入庫日期"
                value={inspectionData.carInDate ? new Date(inspectionData.carInDate).toLocaleDateString() : 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Inspection Date 檢驗日期"
                value={(inspectionData.updatedAt ? new Date(inspectionData.updatedAt) : new Date()).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StyledTextField
                fullWidth
                size="small"
                label="Inspector 檢驗員"
                value={user?.name || user?.email || 'N/A'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </InfoGrid>
        </StyledPaper>

        <Grid container spacing={3}>
          {/* Enhanced Damage Assessment */}
          <Grid item xs={12} lg={8}>
            <StyledPaper elevation={4}>
              <SectionTitle variant="h5">
                <Info sx={{ fontSize: 28 }} />
                損傷評估 Damage Assessment
              </SectionTitle>

              {isSubmitted && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Damage assessment is locked. The inspection has been submitted. Click Re-Inspect to make changes.
                </Alert>
              )}

              {categories.map(category => {
                const categoryTotal = calculateCategoryTotal(category.Name);
                const selectedItemsForCategory = selectedItems[category.Name] || [];
                const availableItems = formItems[category.Name]?.filter(item => 
                  !selectedItemsForCategory.includes(item.Id)
                ) || [];

                // Count items with count > 0
                const activeItemsCount = selectedItemsForCategory.filter(itemId => {
                  const detail = inspectionData.details[itemId];
                  return detail && detail.count > 0;
                }).length;

                return (
                  <StyledAccordion key={category.Id}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                        <Typography variant="h6">
                          {category.Name}
                          <Chip 
                            label={`${activeItemsCount} active`}
                            size="small"
                            color={activeItemsCount > 0 ? "success" : "default"}
                            sx={{ 
                              ml: 2,
                              backgroundColor: activeItemsCount > 0 ? '#4caf50' : undefined,
                              color: activeItemsCount > 0 ? 'white' : undefined,
                              '& .MuiChip-label': {
                                fontWeight: activeItemsCount > 0 ? 600 : 400
                              }
                            }}
                          />
                        </Typography>
                        <Chip
                          label={`Total: ${categoryTotal.toFixed(2)}`}
                          color={categoryTotal >= 0.33 ? "error" : categoryTotal > 0 ? "warning" : "default"}
                          size="small"
                          sx={{ 
                            mr: 1,
                            backgroundColor: categoryTotal >= 0.33 ? '#f44336' : 
                                           categoryTotal > 0 ? '#ff9800' : undefined,
                            color: categoryTotal > 0 ? 'white' : undefined,
                            '& .MuiChip-label': {
                              fontWeight: categoryTotal > 0 ? 600 : 400
                            }
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Damage Code</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Count</TableCell>
                              <TableCell>Weight</TableCell>
                              <TableCell>Score Deduction</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedItemsForCategory.map((itemId, index) => {
                              const item = formItems[category.Name]?.find(i => i.Id === itemId);
                              const detail = inspectionData.details[itemId];
                              if (!item) return null;
                              
                              return (
                                <TableRow key={itemId}>
                                  <TableCell>
                                    <FormControl size="small" sx={{ minWidth: 80 }}>
                                      <Select
                                        value={itemId}
                                        onChange={(e) => updateSelectedItem(category.Name, itemId, e.target.value)}
                                        disabled={isSubmitted}
                                      >
                                        <MenuItem value={itemId}>{item.DamageCode}</MenuItem>
                                        {availableItems.map(availableItem => (
                                          <MenuItem key={availableItem.Id} value={availableItem.Id}>
                                            {availableItem.DamageCode}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                      <Select
                                        value={itemId}
                                        onChange={(e) => updateSelectedItem(category.Name, itemId, e.target.value)}
                                        disabled={isSubmitted}
                                      >
                                        <MenuItem value={itemId}>{item.Description}</MenuItem>
                                        {availableItems.map(availableItem => (
                                          <MenuItem key={availableItem.Id} value={availableItem.Id}>
                                            {availableItem.Description}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell>
                                    <StyledTextField
                                      type="number"
                                      size="small"
                                      value={detail?.count || 0}
                                      onChange={(e) => handleDetailChange(itemId, e.target.value)}
                                      inputProps={{ min: 0, max: 100 }}
                                      sx={{ width: 80 }}
                                      disabled={isSubmitted}
                                    />
                                  </TableCell>
                                  <TableCell>{item.Weight}</TableCell>
                                  <TableCell>{detail?.scoreDeduction?.toFixed(2) || '0.00'}</TableCell>
                                  <TableCell>
                                    <Tooltip title="Remove Item">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => removeItemFromCategory(category.Name, itemId)}
                                          disabled={isSubmitted || selectedItemsForCategory.length <= 1}
                                          color="error"
                                        >
                                          <Delete />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                        <Box>
                          {availableItems.length > 0 && (
                            <Button
                              size="small"
                              startIcon={<Add />}
                              onClick={() => {
                                if (availableItems.length > 0) {
                                  addItemToCategory(category.Name, availableItems[0].Id);
                                }
                              }}
                              disabled={isSubmitted || availableItems.length === 0}
                              variant="outlined"
                            >
                              Add Item
                            </Button>
                          )}
                        </Box>
                        <Box>
                          <Typography 
                            variant="body2" 
                            color={categoryTotal > 0 ? "textPrimary" : "textSecondary"}
                            sx={{ 
                              fontWeight: categoryTotal > 0 ? 600 : 400,
                              color: categoryTotal >= 0.33 ? '#f44336' : 
                                     categoryTotal > 0 ? '#ff9800' : undefined
                            }}
                          >
                            Category Total: <strong>{categoryTotal.toFixed(2)}</strong>
                            {categoryTotal >= 0.33 && (
                              <Chip label="MAX REACHED" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </StyledAccordion>
                );
              })}

              {/* Enhanced Final Assessment */}
              <Box sx={{ mt: 4 }}>
                <SubSectionTitle variant="h6">
                  Final Assessment 最終評估
                </SubSectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <StyledTextField
                      fullWidth
                      size="small"
                      label="Exterior Score 車身分數"
                      value={`${inspectionData.exteriorScore} / 5`}
                      InputProps={{ readOnly: true }}
                      helperText="Auto-calculated based on damage assessment"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <StyledFormControl fullWidth size="small">
                      <InputLabel>Interior Score 內裝分數</InputLabel>
                      <Select
                        value={inspectionData.interiorGrade}
                        onChange={(e) => setInspectionData(prev => ({
                          ...prev,
                          interiorGrade: e.target.value
                        }))}
                        disabled={isSubmitted}
                      >
                        <MenuItem value="A">A - Excellent</MenuItem>
                        <MenuItem value="B">B - Good</MenuItem>
                        <MenuItem value="C">C - Fair</MenuItem>
                      </Select>
                    </StyledFormControl>
                  </Grid>
                </Grid>
              </Box>
            </StyledPaper>
          </Grid>

          {/* Part 3: Enhanced Damage Annotation */}
          <Grid item xs={12} lg={4}>
            <StyledPaper elevation={4}>
              <SectionTitle variant="h5">
                <Create sx={{ fontSize: 28 }} />
                損傷標記 Damage Annotation
              </SectionTitle>
              
              <ImageAnnotationPanel
                vehicleImagePath="/Vehicle.jpg"
                onImageAnnotated={setAnnotatedImage}
                contractNo={inspectionData.contractNo}
                vehicleId={inspectionData.vehicleId}
                onAnnotationStatusChange={setHasUnsavedAnnotations}
                disabled={isSubmitted}
                isSubmitted={isSubmitted}
                wasOriginallySubmitted={wasOriginallySubmitted}
                isReInspection={isReInspection}
                isUnlockedForEditing={isUnlockedForEditing}
              />
            </StyledPaper>
          </Grid>
        </Grid>

        {/* Action Buttons with Notifications */}
        <Box sx={{ mt: 4, mb: 10, textAlign: 'center' }}>
          {/* Unified notification system - supports error, success, info, warning */}
          {notification.message && (
            <Alert 
              severity={notification.type === 'error' ? 'error' : 
                       notification.type === 'warning' ? 'warning' :
                       notification.type === 'info' ? 'info' : 'success'} 
              sx={{ 
                mb: 3, 
                width: 'fit-content',
                maxWidth: '90%', 
                mx: 'auto',
                borderRadius: 2,
                textAlign: 'left',
                '& .MuiAlert-message': {
                  fontSize: '1rem',
                  fontWeight: 500,
                },
              }}
            >
              <Typography variant="body1" component="span" sx={{ fontWeight: 600 }}>
                {notification.type === 'error' ? (
                  notification.message.includes('Download') ? 'Download Failed. ' : 
                  notification.message.includes('unlock') ? 'Unlock Failed. ' : 'Error. '
                ) : notification.type === 'warning' ? (
                  'Warning: '
                ) : notification.type === 'info' ? (
                  'Information: '
                ) : (
                  notification.message.includes('downloaded') ? 'Download Complete! ' :
                  notification.message.includes('unlocked') ? 'Success! ' : 'Success! '
                )}
              </Typography>
              {notification.message}
            </Alert>
          )}
          
          {/* Unlock For Editing Button - Show when inspection is submitted */}
          {isSubmitted && (
            <Button
              variant="contained"
              size="large"
              startIcon={unlockingForEdit ? <CircularProgress size={20} /> : <Edit />}
              onClick={handleUnlockForEditing}
              disabled={unlockingForEdit || unlocking}
              sx={{ 
                mr: 2,
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
                },
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              }}
            >
              {unlockingForEdit ? 'Processing...' : 'Unlock For Editing'}
            </Button>
          )}

          {/* Re-Inspect Button - Show when inspection is submitted */}
          {isSubmitted && (
            <Button
              variant="contained"
              size="large"
              startIcon={unlocking ? <CircularProgress size={20} /> : <LockOpen />}
              onClick={handleUnlock}
              disabled={unlocking || unlockingForEdit}
              sx={{ 
                mr: 2,
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
                },
                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
              }}
            >
              {unlocking ? 'Processing...' : 'Re-Inspect'}
            </Button>
          )}

          {/* Submit Button - Hide when submitted */}
          {!isSubmitted && (
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleSubmit}
              disabled={saving}
              sx={{ mr: 2 }}
            >
              {saving ? 'Saving...' : (isReInspection ? 'Submit Re-Inspection' : 'Submit Inspection')}
            </Button>
          )}
          
          {showDownloadButton && (
            <Button
              variant="outlined"
              size="large"
              startIcon={downloading ? <CircularProgress size={20} /> : <Download />}
              onClick={handleDownloadPDF}
              disabled={downloading}
              sx={{ mr: 2 }}
            >
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          )}
          
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/')}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default InspectionPage;